import React from "react";
import { IVRFlowTypeEnum, WidgetTypeEnum } from "../../../common/enum";
import { INextActionWidget } from "../../../common/interfaces";
import {
	Grid,
	Icon,
	TextField,
	Tooltip,
	Card,
	CardContent,
	Box,
	Select,
	MenuItem,
	ListItemText,
	FormControlLabel,
	Checkbox,
	InputLabel,
	InputAdornment,
	FormControl,
	Input,
	FormHelperText,
} from "../../../common/MaterialComponent";
import { ButtonPrimary } from "../../../common/sharedStyle";
import ResponseMapModel from "../../../models/RequestForHelpTemplates/ResponseMapModel";
import { NextActionSelection } from "../NextActionSelection/NextActionSelection";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

interface ResponseMapWidgetProps {
	responseMap: ResponseMapModel[];
	widgetList: INextActionWidget[];
	addNewResponseMapDetail: () => void;
	updateResponseMapDetail: (
		value: string,
		propertyName: ResponseMapPropertyType,
		index: number
	) => void;
	deleteResponseMapDetail: (index: number) => void;
	widgetType?: WidgetTypeEnum | string;
	ivrFlowType?: IVRFlowTypeEnum | string;
	isVoiceNoteAllowed?: boolean;
	handleIsVoiceNoteAllowedChange?: (isChecked: boolean) => void;
	highlightRequiredDetail?: boolean;
}

export type ResponseMapPropertyType =
	| "value"
	| "comparison"
	| "variableValue"
	| "nextAction";

export const ResponseMapWidget: React.FC<ResponseMapWidgetProps> = ({
	responseMap,
	widgetList,
	addNewResponseMapDetail,
	updateResponseMapDetail,
	deleteResponseMapDetail,
	widgetType,
	ivrFlowType,
	isVoiceNoteAllowed,
	handleIsVoiceNoteAllowedChange,
	highlightRequiredDetail,
}) => {
	const strings = useGetComponentStringsContext().ResponseMapWidget;
	const comparisonTypeList: string[] = [
		"equal_to",
		"matches_any_of",
		"does_not_match_any_of",
	];

	const onComparisonTypeChange = (
		value: string,
		propertyName: ResponseMapPropertyType,
		index: number
	) => {
		updateResponseMapDetail(value, propertyName, index);
		// if selected type is "equal_to", reset comparison value field
		if (value === comparisonTypeList[0]) {
			updateResponseMapDetail("", "value", index);
		}
	};

	const validateInput = (
		event: any,
		propertyName: ResponseMapPropertyType,
		index: number
	) => {
		const isMultipleValueAllowed: boolean = responseMap[index].comparison
			? responseMap[index].comparison !== comparisonTypeList[0]
			: false;
		let regex: RegExp;
		switch (ivrFlowType) {
			case IVRFlowTypeEnum.USSD:
				// allowed values -> if single value -> 0 to 9, if multiple values -> 0,1,2,3,4,...
				regex = !isMultipleValueAllowed ? /^\d{1}$/ : /^(\d{1},)*(\d{1})?$/;
				break;
			case IVRFlowTypeEnum.Voice:
				// allowed values -> if single value -> 0 to 9 or * or #, if multiple values -> *,#,0,1,2,3,4,...
				regex = !isMultipleValueAllowed
					? /^[\d*#]{1}$/
					: /^((\*|#|\d{1}),)*(\*|#|\d{1})?$/;
				break;
			case IVRFlowTypeEnum.SMS:
			case IVRFlowTypeEnum.WhatsApp:
				// allowed values -> if single value -> alphabets,numeric and space,
				// if multiple values -> alphabets,numeric and space separated by comma
				regex = !isMultipleValueAllowed
					? /^[A-Za-z\d\s]+$/
					: /^[A-Za-z\d\s,]+$/;
				break;
			default:
				break;
		}
		if (regex!.test(event.target.value) || event.target.value === "") {
			const value: string = Shared.getSanitizedInputValue(event.target.value);
			updateResponseMapDetail(
				ivrFlowType === IVRFlowTypeEnum.SMS ? value.toUpperCase() : value,
				propertyName,
				index
			);
		} else {
			return;
		}
	};

	return (
		<Grid item xs={12}>
			<Grid container item xs={12}>
				<Grid item xs={11}>
					<h4 className="componentHeader">{strings.mapResponseHeader}</h4>
				</Grid>
				<Grid item xs={1}>
					<Box ml={1}>
						<Tooltip title={strings.addNewResponseTooltip}>
							<ButtonPrimary
								className="addConditionBtn"
								onClick={addNewResponseMapDetail}
							>
								<Icon>add</Icon>
							</ButtonPrimary>
						</Tooltip>
					</Box>
				</Grid>
			</Grid>
			{widgetType === WidgetTypeEnum.AskQuestion &&
			ivrFlowType === IVRFlowTypeEnum.WhatsApp &&
			responseMap.length === 0 ? (
				<>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={isVoiceNoteAllowed}
								onChange={(event) => {
									handleIsVoiceNoteAllowedChange!(event.target.checked);
								}}
							/>
						}
						label={strings.requestVoiceNoteLabel}
					/>
				</>
			) : (
				<>
					{responseMap &&
						responseMap.length > 0 &&
						responseMap.map(
							(responseMapDetail: ResponseMapModel, index: number) => (
								<Grid item xs={12} key={index} className="responseMapGridSpace">
									<Card className="errorTextDiv">
										<CardContent>
											{(widgetType === WidgetTypeEnum.AskQuestion
												? true
												: responseMap.length > 1) && (
												<Grid item xs={12}>
													<Box textAlign="right">
														<Tooltip title={strings.deleteResponseTooltip}>
															<ButtonPrimary
																className="deleteConditionBtn"
																onClick={(event) =>
																	deleteResponseMapDetail(index)
																}
															>
																<Icon>delete</Icon>
															</ButtonPrimary>
														</Tooltip>
													</Box>
												</Grid>
											)}
											<Grid item xs={12} className="responseMapGridSpace">
												<FormControl fullWidth>
													<InputLabel required>
														{strings.comparisonLabel}
													</InputLabel>
													<Select
														value={responseMapDetail.comparison}
														onChange={(event) =>
															onComparisonTypeChange(
																event.target.value as string,
																"comparison",
																index
															)
														}
														fullWidth
														error={
															!responseMapDetail.comparison &&
															highlightRequiredDetail
														}
													>
														{comparisonTypeList.map(
															(option: string, index: number) => (
																<MenuItem key={index} value={option}>
																	<ListItemText
																		primary={option}
																		style={{ textTransform: "uppercase" }}
																	/>
																</MenuItem>
															)
														)}
													</Select>
													{!responseMapDetail.comparison &&
														highlightRequiredDetail && (
															<FormHelperText
																error={
																	!responseMapDetail.comparison &&
																	highlightRequiredDetail
																}
															>
																{strings.comparisonErrorMessage}
															</FormHelperText>
														)}
												</FormControl>
											</Grid>
											<Grid item xs={12} className="responseMapGridSpace">
												<FormControl fullWidth required>
													<InputLabel>
														{strings.comparisonValueLabel}
													</InputLabel>
													<Input
														value={responseMapDetail.value}
														onChange={(event: any) =>
															validateInput(event, "value", index)
														}
														disabled={!responseMapDetail.comparison}
														endAdornment={
															<InputAdornment position="end">
																<Tooltip
																	title={
																		ivrFlowType === IVRFlowTypeEnum.USSD
																			? strings.typeUSSDInfo
																			: ivrFlowType === IVRFlowTypeEnum.Voice
																			? strings.typeVoiceInfo
																			: strings.typeSMSInfo
																	}
																	placement="bottom-end"
																>
																	<Icon color="primary">info</Icon>
																</Tooltip>
															</InputAdornment>
														}
														required
														error={
															!responseMapDetail.value &&
															highlightRequiredDetail
														}
													/>
													<FormHelperText
														error={
															!responseMapDetail.value &&
															highlightRequiredDetail
														}
													>
														{!responseMapDetail.value && highlightRequiredDetail
															? strings.comparisonValueErrorMessage
															: responseMapDetail.comparison &&
															  responseMapDetail.comparison !==
																	comparisonTypeList[0]
															? strings.commaSeparatedMessage
															: ""}
													</FormHelperText>
												</FormControl>
											</Grid>
											<Grid item xs={12} className="responseMapGridSpace">
												<TextField
													label={strings.variableValueLabel}
													value={responseMapDetail.variableValue}
													onChange={(event) =>
														updateResponseMapDetail(
															Shared.getSanitizedInputValue(event.target.value),
															"variableValue",
															index
														)
													}
													fullWidth
													required
													error={
														!responseMapDetail.variableValue &&
														highlightRequiredDetail
													}
													helperText={
														!responseMapDetail.variableValue &&
														highlightRequiredDetail
															? strings.variableValueErrorMessage
															: ""
													}
												/>
											</Grid>
											<Grid item xs={12} className="responseMapGridSpace">
												<NextActionSelection
													widgetList={widgetList}
													selectedWidget={responseMapDetail.nextAction}
													onNextActionChange={(value: string) =>
														updateResponseMapDetail(value, "nextAction", index)
													}
												/>
											</Grid>
											{index === responseMap.length - 1 ? "" : <br />}
										</CardContent>
									</Card>
								</Grid>
							)
						)}
				</>
			)}
		</Grid>
	);
};
